import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Building2, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';

interface CreateVendorModalProps {
  isOpen: boolean;
  onClose: () => void;
  vendorName: string;
  existingVendors: Array<{ id: string; name: string }>;
  onVendorCreated: (vendor: { id: string; name: string }) => void;
}

// Simple fuzzy matching function
function getSimilarVendors(
  searchName: string,
  vendors: Array<{ id: string; name: string }>,
  threshold = 0.3
): Array<{ id: string; name: string; similarity: number }> {
  const searchLower = searchName.toLowerCase().trim();
  
  return vendors
    .map(vendor => {
      const vendorLower = vendor.name.toLowerCase();
      
      // Calculate simple similarity score
      let score = 0;
      
      // Check if one contains the other
      if (vendorLower.includes(searchLower) || searchLower.includes(vendorLower)) {
        score = 0.8;
      } else {
        // Check for common words
        const searchWords = searchLower.split(/\s+/);
        const vendorWords = vendorLower.split(/\s+/);
        const commonWords = searchWords.filter(w => 
          vendorWords.some(vw => vw.includes(w) || w.includes(vw))
        );
        score = commonWords.length / Math.max(searchWords.length, vendorWords.length);
      }
      
      return { ...vendor, similarity: score };
    })
    .filter(v => v.similarity >= threshold)
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, 5);
}

export default function CreateVendorModal({
  isOpen,
  onClose,
  vendorName,
  existingVendors,
  onVendorCreated,
}: CreateVendorModalProps) {
  const { currentWorkspace } = useWorkspace();
  const { toast } = useToast();
  
  const [name, setName] = useState(vendorName);
  const [vendorType, setVendorType] = useState('utility');
  const [isCreating, setIsCreating] = useState(false);

  // Find similar vendors for suggestion
  const similarVendors = useMemo(() => {
    return getSimilarVendors(name, existingVendors);
  }, [name, existingVendors]);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast({
        title: 'Error',
        description: 'Vendor name is required',
        variant: 'destructive',
      });
      return;
    }

    if (!currentWorkspace?.id) {
      toast({
        title: 'Error',
        description: 'No workspace selected',
        variant: 'destructive',
      });
      return;
    }

    setIsCreating(true);
    try {
      const { data, error } = await supabase
        .from('tum_utility_vendors')
        .insert({
          workspace_id: currentWorkspace.id,
          vendor_name: name.trim(),
          vendor_type: vendorType,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: 'Vendor created',
        description: `"${name}" has been added to your vendors`,
      });

      onVendorCreated({ id: data.id, name: data.vendor_name });
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error creating vendor',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleSelectExisting = (vendor: { id: string; name: string }) => {
    toast({
      title: 'Using existing vendor',
      description: `Selected "${vendor.name}"`,
    });
    onVendorCreated(vendor);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Create New Vendor
          </DialogTitle>
          <DialogDescription>
            This vendor was not found in your database. Create it now or select a similar existing vendor.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Similar vendors suggestion */}
          {similarVendors.length > 0 && (
            <div className="p-3 bg-muted/50 rounded-lg border">
              <p className="text-sm font-medium mb-2 flex items-center gap-1">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                Did you mean one of these?
              </p>
              <div className="flex flex-wrap gap-2">
                {similarVendors.map(v => (
                  <Badge 
                    key={v.id} 
                    variant="outline" 
                    className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                    onClick={() => handleSelectExisting(v)}
                  >
                    {v.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="vendorName">Vendor Name</Label>
            <Input
              id="vendorName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter vendor name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="vendorType">Vendor Type</Label>
            <Select value={vendorType} onValueChange={setVendorType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="utility">Utility Provider</SelectItem>
                <SelectItem value="municipal">Municipal</SelectItem>
                <SelectItem value="coop">Cooperative</SelectItem>
                <SelectItem value="private">Private</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={isCreating || !name.trim()}>
            {isCreating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Vendor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
